// =============================================================================
// DATA CACHE SERVICE
// =============================================================================
// Session-based caching for page data from Firebase Storage

import type { PageDataBundle } from './dataLoader';

const CACHE_PREFIX = 'page_cache_';
const CACHE_VERSION_KEY = 'cache_version';
const CACHE_VERSION = '1.0.0';

/**
 * Service for caching page data in sessionStorage
 * Improves performance by avoiding repeated Firebase Storage downloads
 */
export class DataCacheService {
  private useCache: boolean = true;
  
  constructor() {
    // Check if sessionStorage is available
    this.useCache = this.isStorageAvailable();
    
    // Check cache version and clear if outdated
    this.checkCacheVersion();
  }

  /**
   * Check if sessionStorage is available
   */
  private isStorageAvailable(): boolean {
    try {
      const test = '__storage_test__';
      sessionStorage.setItem(test, test);
      sessionStorage.removeItem(test);
      return true;
    } catch (e) {
      // sessionStorage not available, caching disabled
      return false;
    }
  }

  /**
   * Check cache version and clear if outdated
   */
  private checkCacheVersion(): void {
    if (!this.useCache) return;

    try {
      const storedVersion = sessionStorage.getItem(CACHE_VERSION_KEY);
      if (storedVersion !== CACHE_VERSION) {
        this.clearAll();
        sessionStorage.setItem(CACHE_VERSION_KEY, CACHE_VERSION);
      }
    } catch (error) {
      // Error checking cache version
    }
  }

  /**
   * Get cache key for a page
   */
  private getCacheKey(pageId: string): string {
    return `${CACHE_PREFIX}${pageId}`;
  }

  /**
   * Get cached page data
   */
  get(pageId: string): PageDataBundle | null {
    if (!this.useCache) return null;

    try {
      const cacheKey = this.getCacheKey(pageId);
      const cached = sessionStorage.getItem(cacheKey);
      
      if (!cached) {
        return null;
      }

      const data = JSON.parse(cached);
      
      // Validate cached data structure
      if (!data || !data.pageId || !Array.isArray(data.components)) {
        // Invalid cached data, clearing
        this.remove(pageId);
        return null;
      }

      return data as PageDataBundle;
    } catch (error) {
      // Error reading cache
      return null;
    }
  }

  /**
   * Set cached page data
   */
  set(pageId: string, data: PageDataBundle): boolean {
    if (!this.useCache) return false;

    try {
      const cacheKey = this.getCacheKey(pageId);
      const json = JSON.stringify(data);
      
      // Check if data is too large (sessionStorage typically has ~5-10MB limit)
      if (json.length > 4 * 1024 * 1024) {
        // Data too large to cache
        return false;
      }

      sessionStorage.setItem(cacheKey, json);
      return true;
    } catch (error) {
      // Handle quota exceeded error
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        // Session storage quota exceeded, clearing old cache
        this.clearAll();
        
        // Try again after clearing
        try {
          const cacheKey = this.getCacheKey(pageId);
          sessionStorage.setItem(cacheKey, JSON.stringify(data));
          return true;
        } catch (retryError) {
          // Failed to cache after clearing
          return false;
        }
      }
      
      // Error caching
      return false;
    }
  }

  /**
   * Remove cached page data
   */
  remove(pageId: string): void {
    if (!this.useCache) return;

    try {
      const cacheKey = this.getCacheKey(pageId);
      sessionStorage.removeItem(cacheKey);
    } catch (error) {
      // Error removing cache
    }
  }

  /**
   * Clear all cached page data
   */
  clearAll(): void {
    if (!this.useCache) return;

    try {
      const keys = Object.keys(sessionStorage);
      const cacheKeys = keys.filter(key => key.startsWith(CACHE_PREFIX));
      
      cacheKeys.forEach(key => {
        sessionStorage.removeItem(key);
      });
      
    } catch (error) {
      // Error clearing cache
    }
  }

  /**
   * Get cache statistics
   */
  getStats(): { totalItems: number; totalSize: number; items: { pageId: string; size: number }[] } {
    if (!this.useCache) {
      return { totalItems: 0, totalSize: 0, items: [] };
    }

    try {
      const keys = Object.keys(sessionStorage);
      const cacheKeys = keys.filter(key => key.startsWith(CACHE_PREFIX));
      
      let totalSize = 0;
      const items = cacheKeys.map(key => {
        const value = sessionStorage.getItem(key);
        const size = value ? value.length : 0;
        totalSize += size;
        
        return {
          pageId: key.replace(CACHE_PREFIX, ''),
          size,
        };
      });

      return {
        totalItems: items.length,
        totalSize,
        items,
      };
    } catch (error) {
      // Error getting cache stats
      return { totalItems: 0, totalSize: 0, items: [] };
    }
  }

  /**
   * Check if a page is cached
   */
  has(pageId: string): boolean {
    if (!this.useCache) return false;

    try {
      const cacheKey = this.getCacheKey(pageId);
      return sessionStorage.getItem(cacheKey) !== null;
    } catch (error) {
      return false;
    }
  }

  /**
   * Preload multiple pages into cache
   */
  async preload(pageIds: string[], loader: (pageId: string) => Promise<PageDataBundle | null>): Promise<void> {
    if (!this.useCache) return;

    
    const promises = pageIds.map(async pageId => {
      if (this.has(pageId)) {
        return;
      }

      try {
        const data = await loader(pageId);
        if (data) {
          this.set(pageId, data);
        }
      } catch (error) {
        // Error preloading
      }
    });

    await Promise.all(promises);
  }
}

// Export singleton instance
export const dataCache = new DataCacheService();

// Export class for custom instances
export default DataCacheService;

